'use client';

// The branded crash screen. Deliberately built on the same `.v404-*` layout as
// the missing-page surface (`NotFound.tsx`): a crash is the other half of "the
// thing you wanted is not here", and a buyer should not be able to tell that one
// was designed and the other was an afterthought. No new CSS.
//
// The copy is passed in rather than branched on inside, because the three
// callers are talking to three different people: a buyer mid-tour on a stranger's
// listing, an agent who may have just lost unsaved work, and the backstop that
// does not know which. See each error.tsx for the wording it chose and why.

import React from 'react';
import Link from 'next/link';
import { VireoMark, VireoWordmark } from './brand';

export function ErrorSurface({
  eyebrow,
  heading,
  sub,
  retryLabel = 'Try again',
  onRetry,
  secondaryHref,
  secondaryLabel,
}: {
  eyebrow: string;
  heading: string;
  /** Say plainly whether their work survived. Vagueness here is what makes a
   *  crash screen frightening; an agent who does not know cannot decide whether
   *  to retype something. */
  sub: string;
  retryLabel?: string;
  onRetry: () => void;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  return (
    <div className="v-app v404">
      <header className="v404-head">
        <Link href="/" aria-label="Vireo home" style={{ textDecoration: 'none' }}>
          <VireoWordmark size={22} />
        </Link>
      </header>

      <main className="v404-main">
        {/* Same wall-size mark at texture strength as the 404, ink not amber,
            for the same reason: at five percent over navy, amber reads as a
            smudge rather than the signal colour. */}
        <VireoMark
          size={150}
          color="var(--v-ink)"
          dot="var(--v-ink)"
          style={{
            position: 'absolute',
            width: 'min(132%, 1100px)',
            height: 'auto',
            opacity: 0.05,
            pointerEvents: 'none',
          }}
        />

        <div className="v404-body">
          <p className="v-eyebrow">{eyebrow}</p>

          {/* .v404-line hides every span but the first; one span is the whole
              line here, since a crash is not the place for a rotating joke. */}
          <h1 className="v-display v404-line">
            <span>{heading}</span>
          </h1>

          <p className="v404-sub">{sub}</p>

          <div className="v404-actions">
            <button type="button" className="v-btn is-amber is-lg" onClick={onRetry}>
              {retryLabel}
            </button>
            {secondaryHref && secondaryLabel && (
              <Link href={secondaryHref} className="v-btn is-ghost is-lg">
                {secondaryLabel}
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
